import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import UserContext from '../../Shared/UserContext';
import DeletePopup from '../../components/DeletePopup';
import AppointmentForm from './AppointmentForm';
import { secureDelete } from '../../config/api';
import xIcon from '../../icons/x.svg';
import editIcon from '../../icons/editB.svg';
import deleteIcon from '../../icons/trashW.svg';

const AppointmentDetails = ({ appointment, onClose, onRefresh }) => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);

  const canEdit = user?.role === 'ROLE_ADMIN' || user?.role === 'ROLE_SERVICE_CONSULTANT';

  const handleEdit = () => {
    setShowEditForm(true);
  };

  const handleDelete = () => {
    setShowDeletePopup(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await secureDelete(`/appointments/${appointment.id}`, () => {});
      setShowDeletePopup(false);
      onClose();
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error deleting appointment:', error);
    }
  };

  const handleEditFormClose = () => {
    setShowEditForm(false);
  };

  const handleEditFormSave = () => {
    setShowEditForm(false);
    onClose();
    if (onRefresh) {
      onRefresh();
    }
  };

  if (showEditForm) {
    return (
      <AppointmentForm
        appointment={appointment}
        onSave={handleEditFormSave}
        onClose={handleEditFormClose}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-[1050]">
      <div className="bg-white rounded-[20px] w-[600px] border-2 border-primary p-6 relative shadow-xl">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-[25px] font-bold text-primary pr-8">
            {appointment.title}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          >
            <img src={xIcon} alt="Close" className="w-5 h-5" />
          </button>
        </div>

        {/* Appointment Details */}
        <div className="">
          {/* Client, Vehicle, Date - Same Line */}
          <div className="grid grid-cols-3 gap-4 mb-2">
            {/* Client */}
            <div>
              <p className="text-[14px] font-semibold text-[#707070] uppercase tracking-wide">CLIENT</p>
              <p className="text-[20px] font-semibold text-[#38485C]">
                {appointment.clientName || '—'}
              </p>
            </div>

            {/* Vehicle */}
            <div>
              <p className="text-[14px] font-semibold text-[#707070] uppercase tracking-wide">VÉHICULE</p>
              <p className="text-[20px] font-semibold text-[#38485C]">
                {appointment.vehicleImmatriculationNumber || '—'}
              </p>
            </div>

            {/* Date */}
            <div>
              <p className="text-[14px] font-semibold text-[#707070] uppercase tracking-wide">DATE</p>
              <p className="text-[20px] font-semibold text-[#38485C]">
                {moment(appointment.appointmentDate || appointment.start).format('DD/MM/YYYY HH:mm:ss')}
              </p>
            </div>
          </div>

          {/* Separator Line */}
          <div className="border-t border-gray-200 mb-2"></div>

          {/* Description */}
          {appointment.description && (
            <div>
              <p className="text-[14px] font-semibold text-[#707070] uppercase tracking-wide">DESCRIPTION</p>
              <p className="text-[20px] font-semibold text-[#38485C] leading-relaxed">
                {appointment.description}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 justify-center pt-4">
            <button
              onClick={handleEdit}
              className="flex items-center justify-center px-4 py-2 bg-white text-primary border border-primary opacity-100 rounded-[12px] font-bold text-[12px] shadow-[0px_20px_80px_#00000011]"
            >
              <img src={editIcon} alt="" className="w-4 h-4 mr-2" />
              {t('actions.modify')}
            </button>
            <button
              onClick={handleDelete}
              className="flex items-center justify-center px-4 py-2 bg-[#EF466F] text-white rounded-[12px] font-semibold text-[12px] shadow-[0px_20px_80px_#00000011]"
            >
              <img src={deleteIcon} alt="" className="w-4 h-4 mr-2" />
              {t('actions.delete')}
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Popup */}
      {showDeletePopup && (
        <DeletePopup
          open={showDeletePopup}
          onCancel={() => setShowDeletePopup(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
};

export default AppointmentDetails;

